import { AssignmentReturn, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RequisitionResponse } from "../../../types/model/warehouse/requisition";
import { todayISO } from "../../../utils/date";
import EquipmentReturnList from "./EquipmentReturnList";
import PeriodCell from "./PeriodCell";
import RequisitionForm from "./RequisitionForm";

export type RequisitionStatus = "returned" | "overdue" | "partial" | "active";

export const STATUS_COLORS = {
  returned: "default",
  overdue: "error",
  partial: "info",
  active: "success",
} as const;

export const getRequisitionStatus = (row: RequisitionResponse): RequisitionStatus => {
  if (row.returnedOn) return "returned";
  if (row.endDate && row.endDate.slice(0, 10) < todayISO()) return "overdue";
  if (row.equipment.some((e) => e.returnedOn)) return "partial";
  return "active";
};

interface RequisitionRowProps {
  row: RequisitionResponse;
  open: boolean;
  canEdit: boolean;
  columnCount: number;
  onToggle: () => void;
  onReturn: (requisitionId: number, equipmentIds: number[]) => Promise<void>;
  onDownloadGuide: (requisitionId: number) => Promise<void>;
  onSendGuide: (requisitionId: number) => Promise<void>;
}

export default function RequisitionRow({
  row,
  open,
  canEdit,
  columnCount,
  onToggle,
  onReturn,
  onDownloadGuide,
  onSendGuide,
}: Readonly<RequisitionRowProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isReturning, setIsReturning] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [guideAction, setGuideAction] = useState<"download" | "email" | null>(null);

  const status = getRequisitionStatus(row);
  const pendingIds = row.equipment.filter((e) => !e.returnedOn).map((e) => e.equipmentId);
  const [first, ...others] = row.equipment;

  const toggleSelected = (equipmentId: number) =>
    setSelectedIds((prev) =>
      prev.includes(equipmentId) ? prev.filter((id) => id !== equipmentId) : [...prev, equipmentId],
    );

  const toggleAll = () => setSelectedIds((prev) => (prev.length === pendingIds.length ? [] : pendingIds));

  // Always send explicit IDs: the account may only see part of the equipment
  const handleReturn = async (equipmentIds: number[]) => {
    if (equipmentIds.length === 0) return;
    try {
      setIsReturning(true);
      await onReturn(row.id, equipmentIds);
      setSelectedIds([]);
    } catch {
      // Error already handled by parent, keep the selection
    } finally {
      setIsReturning(false);
    }
  };

  const handleGuide = async (action: "download" | "email") => {
    try {
      setGuideAction(action);
      await (action === "download" ? onDownloadGuide(row.id) : onSendGuide(row.id));
    } catch {
      // Error already handled by parent
    } finally {
      setGuideAction(null);
    }
  };

  return (
    <React.Fragment>
      <TableRow onClick={onToggle} sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}>
        <TableCell>{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {first?.equipmentType}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[first?.equipmentModel, first?.serialNumber].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            {others.length > 0 && <Chip size="small" variant="outlined" label={`+${others.length}`} />}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{row.requestedByName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.accountName}
          </Typography>
        </TableCell>
        <TableCell>
          <PeriodCell startDate={row.startDate} endDate={row.endDate} overdue={status === "overdue"} />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            color={STATUS_COLORS[status]}
            variant={status === "returned" ? "outlined" : "filled"}
            label={t(`warehouse.requisitions.statuses.${status}`)}
          />
        </TableCell>
        {canEdit && (
          <TableCell align="right">
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "flex-end" }}>
              {pendingIds.length > 0 && (
                <Tooltip title={t("warehouse.requisitions.return-all")}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleReturn(pendingIds)}
                    disabled={isReturning}
                  >
                    {isReturning ? <CircularProgress size={16} /> : <AssignmentReturn fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, display: "flex", flexDirection: "column", gap: 3 }}>
              <EquipmentReturnList
                equipment={row.equipment}
                canEdit={canEdit}
                selectedIds={selectedIds}
                isReturning={isReturning}
                onToggle={toggleSelected}
                onToggleAll={toggleAll}
                onReturn={handleReturn}
              />

              {/* Transport guide actions */}
              {row.transportGuide && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      guideAction === "download" ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
                    }
                    onClick={() => handleGuide("download")}
                    disabled={!!guideAction}
                  >
                    {t("warehouse.requisitions.transport-guide.download")}
                  </Button>
                  {canEdit && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        guideAction === "email" ? <CircularProgress size={14} color="inherit" /> : <EmailIcon />
                      }
                      onClick={() => handleGuide("email")}
                      disabled={!!guideAction}
                    >
                      {t("warehouse.requisitions.transport-guide.send-email")}
                    </Button>
                  )}
                </Box>
              )}

              <RequisitionForm formId={`form-${row.id}`} requisitionData={row} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}